import React, { useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import classNames from 'classnames';

import { FormattedMessage } from '../../../util/reactIntl';
import { useRouteConfiguration } from '../../../context/routeConfigurationContext';
import { createResourceLocatorString } from '../../../util/routes';
import { LISTING_STATE_CLOSED } from '../../../util/types';
import { formatMoney } from '../../../util/currency';
import { CREATE_CLASS_INFO } from '../../../util/createClass';
import { isProviderUser } from '../../../util/onboarding';
import { Modal, NamedLink } from '../../../components';

import InquiryForm from '../InquiryForm/InquiryForm';
import ImageCarousel from '../ImageCarousel/ImageCarousel';

import ClassHero from './ClassHero';
import ClassHeader from './ClassHeader';
import ClassPhotoStrip from './ClassPhotoStrip';
import ClassQuickInfo from './ClassQuickInfo';
import ClassSchedule from './ClassSchedule';
import ClassAbout from './ClassAbout';
import ClassProviderCard from './ClassProviderCard';
import ClassLocationCard from './ClassLocationCard';
import ClassReviews from './ClassReviews';
import ClassBookingModal from './ClassBookingModal';
import ClassShareModal from './ClassShareModal';
import SimilarClasses from './SimilarClasses';

import css from './ClassDetail.module.css';
import { showsAsTerm, termWeeks, isTermEnrollmentClosed } from '../../../util/termSchedule';

/**
 * The KidHive class detail page body (Figma "Activity Page"), shared by BOTH ListingPage variants so
 * a Console layout switch only changes the hero treatment (image card vs full-bleed cover). The
 * booking engine is untouched underneath: the Book now CTA currently scrolls to the schedule card
 * and is wired to the Activity Booking modal (the restyled OrderPanel form) in a follow-up
 * increment; that modal submits through the stock handleSubmit -> CheckoutPage seam.
 */
const ClassDetailBody = props => {
  const {
    actionBar,
    currentListing,
    currentUser,
    config,
    listingConfig,
    intl,
    fullBleedHero = false,
    isOwnListing = false,
    noPayoutDetailsSetWithOwnListing = false,
    listingUrl,
    monthlyTimeSlots,
    similarListings,
    reviews,
    fetchReviewsError,
    authorDisplayName,
    onContactUser,
    isInquiryModalOpen,
    onCloseInquiryModal,
    sendInquiryError,
    sendInquiryInProgress,
    onSubmitInquiry,
    onManageDisableScrolling,
    onOrderSubmit,
    onFetchTimeSlots,
  } = props;

  const [carouselOpen, setCarouselOpen] = useState(false);
  const [bookingOpen, setBookingOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  // Day + slot picked on the on-page schedule card; opens the booking modal preselected. The modal
  // is keyed by this, so each new pick remounts it with fresh state.
  const [bookingPreselect, setBookingPreselect] = useState(null);

  // Return focus to whichever element opened a modal (Book now, a schedule slot, the gallery,
  // share, contact). One remembered trigger covers every entry point without per-button ids;
  // keyboard users always have the trigger focused at open time.
  const modalTriggerRef = useRef(null);
  const rememberModalTrigger = () => {
    modalTriggerRef.current = typeof document !== 'undefined' ? document.activeElement : null;
  };
  const restoreModalTrigger = () => {
    if (modalTriggerRef.current && typeof modalTriggerRef.current.focus === 'function') {
      modalTriggerRef.current.focus();
    }
    modalTriggerRef.current = null;
  };

  const onOpenGallery = () => {
    rememberModalTrigger();
    setCarouselOpen(true);
  };
  // Class pages are public, but booking is not: an anonymous visitor who taps a booking CTA is sent
  // to login/signup, returning to this class afterward (state.from). Logged-in users book as usual.
  const history = useHistory();
  const routeConfiguration = useRouteConfiguration();
  const notLoggedIn = !currentUser?.id;
  const redirectToLogin = () => {
    const from =
      typeof window !== 'undefined' ? `${window.location.pathname}${window.location.search}` : null;
    history.push(createResourceLocatorString('LoginPage', routeConfiguration, {}, {}), {
      from,
    });
  };
  const onBookNow = () => {
    if (notLoggedIn) {
      redirectToLogin();
      return;
    }
    rememberModalTrigger();
    setBookingOpen(true);
  };
  const onOpenShare = () => {
    rememberModalTrigger();
    setShareOpen(true);
  };
  const onBookSlot = (dayKey, slotKey) => {
    if (notLoggedIn) {
      redirectToLogin();
      return;
    }
    rememberModalTrigger();
    setBookingPreselect({ dayKey, slotKey });
    setBookingOpen(true);
  };
  const onContactUserWithFocus = (...args) => {
    if (notLoggedIn) {
      redirectToLogin();
      return undefined;
    }
    rememberModalTrigger();
    return onContactUser(...args);
  };

  const images = currentListing.images || [];
  const { title = '', price, publicData = {}, state } = currentListing.attributes || {};
  // Booking CTAs hide for the provider's own listing, for closed listings, and for providers in
  // general (providers preview classes, they never book; the checkout gate is the backstop).
  const isProvider = isProviderUser(currentUser);
  // A multi-week class closes to new enrollments once its first session has started (client
  // decision; checkout enforces it too) -- the page says so in place of Book now.
  const termEnrollmentClosed = isTermEnrollmentClosed(currentListing);
  const canBook =
    !isOwnListing && !isProvider && state !== LISTING_STATE_CLOSED && !termEnrollmentClosed;
  // Providers viewing ANY class see the parent-facing page under a slim banner back into the hub:
  // their OWN class = the preview banner (with edit + state chip); someone else's = a provider-view
  // note (booking is parents-only either way).
  const showProviderBanner = isProvider;
  const isOwnerPreview = isOwnListing && isProvider;
  const priceFormatted = price && price.amount != null ? formatMoney(intl, price) : null;

  return (
    <div
      className={classNames(css.body, {
        // The fixed mobile book bar needs its height reserved at the page end.
        [css.bodyWithBookBar]: canBook && !!priceFormatted,
      })}
    >
      {showProviderBanner ? (
        <div className={css.ownerPreviewBanner}>
          <span className={css.ownerPreviewText}>
            <FormattedMessage
              id={isOwnerPreview ? 'ClassDetailPage.ownerPreview' : 'ClassDetailPage.providerView'}
            />
            {isOwnerPreview && state !== 'published' ? (
              <span className={css.ownerPreviewState}>
                <FormattedMessage id={`ProviderActivitiesPage.state.${state}`} />
              </span>
            ) : null}
          </span>
          <span className={css.ownerPreviewLinks}>
            <NamedLink name="ProviderDashboardPage" className={css.ownerPreviewLink}>
              <FormattedMessage id="ClassDetailPage.ownerPreviewBack" />
            </NamedLink>
            {isOwnerPreview ? (
              <NamedLink
                name="CreateClassWizardPage"
                params={{ id: currentListing.id?.uuid, tab: CREATE_CLASS_INFO }}
                className={css.ownerPreviewLink}
              >
                <FormattedMessage id="ClassDetailPage.ownerPreviewEdit" />
              </NamedLink>
            ) : null}
          </span>
        </div>
      ) : null}

      {/* Stock listing action bars: the preview banner replaces the redundant "this is your own
          listing" bar (it carries the edit link + state chip), but the missing-payout-details
          warning must still surface -- a provider without payout details can't get paid -- and the
          closed-listing notice for parents renders as before (no banner in that case). */}
      {actionBar && (!showProviderBanner || noPayoutDetailsSetWithOwnListing) ? (
        <div className={css.actionBar}>{actionBar}</div>
      ) : null}

      {/* The testid mirrors the Console layout variant (cover photo vs carousel) for the tests. */}
      <section className={css.heroCard} data-testid={fullBleedHero ? 'hero' : 'carousel'}>
        {/* Compact hero (client ask): a small contained image DOCKED LEFT with the header beside
            it from the medium viewport -- the whole photo is always visible and the freed space
            carries the title/price up the page. Mobile keeps the stacked order. */}
        <div className={css.heroRow}>
          <ClassHero
            listing={currentListing}
            fullBleed={fullBleedHero}
            onOpenGallery={onOpenGallery}
            onOpenShare={listingUrl ? onOpenShare : null}
          />
          <ClassHeader
            listing={currentListing}
            config={config}
            intl={intl}
            authorDisplayName={authorDisplayName}
            monthlyTimeSlots={monthlyTimeSlots}
            onBookNow={canBook ? onBookNow : null}
            enrollmentClosed={termEnrollmentClosed && !isOwnListing && !isProvider}
          />
        </div>
      </section>

      <ClassPhotoStrip listing={currentListing} onOpenGallery={onOpenGallery} />

      <div className={css.columns}>
        <div className={css.mainColumn}>
          <ClassQuickInfo
            listing={currentListing}
            currentUser={currentUser}
            intl={intl}
          />
          <ClassSchedule
            listing={currentListing}
            monthlyTimeSlots={monthlyTimeSlots}
            intl={intl}
            onBookSlot={canBook ? onBookSlot : null}
            onFetchTimeSlots={onFetchTimeSlots}
          />
          <ClassAbout
            listing={currentListing}
            config={config}
            listingConfig={listingConfig}
            intl={intl}
          />
          <ClassReviews reviews={reviews} />
        </div>

        <div className={css.sideColumn}>
          <ClassProviderCard
            listing={currentListing}
            currentUser={currentUser}
            authorDisplayName={authorDisplayName}
            onContactUser={onContactUserWithFocus}
          />
          <ClassLocationCard
            listing={currentListing}
            currentUser={currentUser}
            mapsConfig={config.maps}
            intl={intl}
          />
        </div>
      </div>

      {images.length > 0 ? (
        <Modal
          id="ClassDetail.imageCarousel"
          scrollLayerClassName={css.carouselModalScrollLayer}
          containerClassName={css.carouselModalContainer}
          lightCloseButton
          isOpen={carouselOpen}
          onClose={() => {
            setCarouselOpen(false);
            restoreModalTrigger();
          }}
          usePortal
          onManageDisableScrolling={onManageDisableScrolling}
        >
          <ImageCarousel
            images={images}
            // Quality floor: the fullscreen carousel only ever considers the two largest
            // variants (xlarge = the API's no-upscale ceiling), so a click always shows the
            // best the upload allows.
            imageVariants={['scaled-large', 'scaled-xlarge']}
          />
        </Modal>
      ) : null}

      <Modal
        id="ClassDetail.inquiry"
        contentClassName={css.inquiryModalContent}
        isOpen={isInquiryModalOpen}
        onClose={() => {
          onCloseInquiryModal();
          restoreModalTrigger();
        }}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
      >
        <InquiryForm
          className={css.inquiryForm}
          submitButtonWrapperClassName={css.inquirySubmitButtonWrapper}
          listingTitle={title}
          authorDisplayName={authorDisplayName}
          sendInquiryError={sendInquiryError}
          onSubmit={onSubmitInquiry}
          inProgress={sendInquiryInProgress}
        />
      </Modal>

      {listingUrl ? (
        <ClassShareModal
          isOpen={shareOpen}
          onClose={() => {
            setShareOpen(false);
            restoreModalTrigger();
          }}
          listingTitle={title}
          listingUrl={listingUrl}
          onManageDisableScrolling={onManageDisableScrolling}
        />
      ) : null}

      {canBook ? (
        <ClassBookingModal
          key={bookingPreselect ? `${bookingPreselect.dayKey}-${bookingPreselect.slotKey}` : 'plain'}
          isOpen={bookingOpen}
          onClose={() => {
            setBookingOpen(false);
            setBookingPreselect(null);
            restoreModalTrigger();
          }}
          listing={currentListing}
          currentUser={currentUser}
          monthlyTimeSlots={monthlyTimeSlots}
          intl={intl}
          onSubmit={onOrderSubmit}
          onFetchTimeSlots={onFetchTimeSlots}
          onManageDisableScrolling={onManageDisableScrolling}
          initialDayKey={bookingPreselect?.dayKey || null}
          initialSlotKey={bookingPreselect?.slotKey || null}
        />
      ) : null}

      {/* Mobile: sticky bottom bar with the price + Book now (the stock OrderPanel pattern in the
          KidHive skin); hidden on desktop and while nothing is bookable. */}
      {canBook && priceFormatted ? (
        <div className={css.mobileBookBar}>
          <div className={css.mobileBookBarPrice}>
            <span className={css.priceAmount}>{priceFormatted}</span>
            <span className={css.priceUnit}>
              {showsAsTerm(publicData) && termWeeks(publicData) ? (
                <FormattedMessage
                  id="ClassDetailPage.priceUnitTermWeeks"
                  values={{ weeks: termWeeks(publicData) }}
                />
              ) : (
                <FormattedMessage
                  id={
                    showsAsTerm(publicData)
                      ? 'ClassDetailPage.priceUnitTerm'
                      : 'ClassDetailPage.priceUnitSession'
                  }
                />
              )}
            </span>
          </div>
          <button type="button" className={css.bookNow} onClick={onBookNow}>
            <FormattedMessage id="ClassDetailPage.bookNow" />
          </button>
        </div>
      ) : null}

      <SimilarClasses listings={similarListings} currentUser={currentUser} />

      {fetchReviewsError ? (
        <p className={css.reviewsError}>
          <FormattedMessage id="ClassDetailPage.reviewsError" />
        </p>
      ) : null}
    </div>
  );
};

export default ClassDetailBody;
